import inventoryService from './inventoryService';

const STORAGE_KEY = 'hygo_pharmacy_excel_gemini_import_v1';

export type GeminiImportPhase = 'idle' | 'uploading' | 'polling' | 'completed' | 'failed';

export type GeminiImportResultData = {
  products: Array<{ product: Record<string, unknown>; status?: string }>;
  skippedDetails: unknown[];
  skipped?: number;
  total?: number;
};

export type GeminiImportProgress = {
  completedChunks: number;
  totalChunks: number;
  rowsCoveredSoFar?: number;
  totalExcelRows?: number;
};

export type GeminiImportSnapshot = {
  phase: GeminiImportPhase;
  jobId: string | null;
  error: string | null;
  result: { success: true; data: GeminiImportResultData } | null;
  /** Latest server-side preview while status is processing (e.g. every 100 Excel rows). */
  progress: GeminiImportProgress | null;
  partial: { success: true; data: GeminiImportResultData } | null;
};

type Listener = () => void;
const listeners = new Set<Listener>();

let snapshot: GeminiImportSnapshot = {
  phase: 'idle',
  jobId: null,
  error: null,
  result: null,
  progress: null,
  partial: null,
};

let pollAbort = false;
/** Monotonic session so a new import invalidates an in-flight poll loop. */
let currentImportSession = 0;
let pollingActiveForSession: number | null = null;

const POLL_INTERVAL_MS = 2000;
const POLL_DEADLINE_MS = 3 * 60 * 60 * 1000;

function emit() {
  listeners.forEach((fn) => fn());
}

function setSnapshot(partial: Partial<GeminiImportSnapshot>) {
  snapshot = { ...snapshot, ...partial };
  emit();
}

function persistJobId(jobId: string) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ jobId, savedAt: Date.now() }));
  } catch {
    /* quota / private mode */
  }
}

function readPersistedJobId(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as { jobId?: string };
    return o.jobId && typeof o.jobId === 'string' ? o.jobId : null;
  } catch {
    return null;
  }
}

function clearPersistedJobId() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getGeminiImportSnapshot(): GeminiImportSnapshot {
  return snapshot;
}

export function subscribeGeminiImport(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function cancelGeminiImportPolling() {
  pollAbort = true;
  console.log('[excel-import-manager] Polling cancelled');
}

async function runPollLoop(jobId: string, session: number): Promise<void> {
  pollAbort = false;
  pollingActiveForSession = session;
  const deadline = Date.now() + POLL_DEADLINE_MS;

  try {
    while (!pollAbort && Date.now() < deadline) {
      if (session !== currentImportSession) return;

      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (pollAbort) return;
      if (session !== currentImportSession) return;

      const { ok, body } = await inventoryService.fetchImportExcelJobStatus(jobId);
      console.log('[excel-import-manager] Poll tick:', {
        jobId,
        ok,
        status: (body?.data as Record<string, unknown> | undefined)?.status,
      });
      if (!ok) {
        if (session !== currentImportSession) return;
        clearPersistedJobId();
        setSnapshot({
          phase: 'failed',
          jobId: null,
          error:
            (typeof body?.message === 'string' && body.message) ||
            'Failed to check import status.',
          result: null,
          progress: null,
          partial: null,
        });
        return;
      }

      const data = body.data as Record<string, unknown> | undefined;
      const status = data?.status as string | undefined;

      if (status === 'completed' && body.success) {
        console.log('[excel-import-manager] Job completed:', { jobId });
        if (session !== currentImportSession) return;
        clearPersistedJobId();
        setSnapshot({
          phase: 'completed',
          jobId: null,
          error: null,
          result: {
            success: true,
            data: {
              products: (data?.products as GeminiImportResultData['products']) || [],
              skippedDetails: (data?.skippedDetails as unknown[]) || [],
              skipped: data?.skipped as number | undefined,
              total: data?.total as number | undefined,
            },
          },
          progress: null,
          partial: null,
        });
        return;
      }

      if (status === 'failed' || body.success === false) {
        console.error('[excel-import-manager] Job failed:', {
          jobId,
          message: body.message,
        });
        if (session !== currentImportSession) return;
        clearPersistedJobId();
        setSnapshot({
          phase: 'failed',
          jobId: null,
          error: (typeof body.message === 'string' && body.message) || 'Import failed.',
          result: null,
          progress: null,
          partial: null,
        });
        return;
      }

      if (status === 'processing' || status === 'queued') {
        const partialRaw = data?.partial as Record<string, unknown> | null | undefined;
        const progressRaw = data?.progress as Record<string, unknown> | null | undefined;
        if (
          partialRaw &&
          progressRaw &&
          typeof progressRaw.completedChunks === 'number' &&
          session === currentImportSession
        ) {
          const completed = progressRaw.completedChunks as number;
          if (
            snapshot.progress?.completedChunks !== completed ||
            snapshot.partial === null
          ) {
            console.log('[excel-import-manager] Partial update received:', {
              jobId,
              completedChunks: completed,
              totalChunks: (progressRaw.totalChunks as number) || 1,
              rowsCoveredSoFar: progressRaw.rowsCoveredSoFar,
              totalExcelRows: progressRaw.totalExcelRows,
            });
            setSnapshot({
              phase: 'polling',
              jobId,
              error: null,
              progress: {
                completedChunks: completed,
                totalChunks: (progressRaw.totalChunks as number) || 1,
                rowsCoveredSoFar: progressRaw.rowsCoveredSoFar as number | undefined,
                totalExcelRows: progressRaw.totalExcelRows as number | undefined,
              },
              partial: {
                success: true,
                data: {
                  products: (partialRaw.products as GeminiImportResultData['products']) || [],
                  skippedDetails: (partialRaw.skippedDetails as unknown[]) || [],
                  skipped: partialRaw.skipped as number | undefined,
                  total: partialRaw.total as number | undefined,
                },
              },
            });
          }
        }
      }
    }

    if (pollAbort) return;
    if (session !== currentImportSession) return;

    clearPersistedJobId();
    setSnapshot({
      phase: 'failed',
      jobId: null,
      error: 'Import is taking too long. Try a smaller file or try again later.',
      result: null,
      progress: null,
      partial: null,
    });
  } finally {
    if (pollingActiveForSession === session) {
      pollingActiveForSession = null;
    }
  }
}

/**
 * Start upload + background polling. Safe to call while navigating away from Product Master.
 * A new import cancels polling for any previous job.
 */
export async function startGeminiExcelImport(file: File): Promise<void> {
  currentImportSession += 1;
  const session = currentImportSession;
  cancelGeminiImportPolling();
  clearPersistedJobId();
  setSnapshot({
    phase: 'uploading',
    jobId: null,
    error: null,
    result: null,
    progress: null,
    partial: null,
  });

  await new Promise((r) => setTimeout(r, 0));

  try {
    console.log('[excel-import-manager] Starting upload:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    });
    const jobId = await inventoryService.postExcelImportJob(file);
    if (session !== currentImportSession) return;
    console.log('[excel-import-manager] Job accepted:', { jobId });
    persistJobId(jobId);
    setSnapshot({
      phase: 'polling',
      jobId,
      error: null,
      result: null,
      progress: null,
      partial: null,
    });
    await runPollLoop(jobId, session);
  } catch (e) {
    if (session !== currentImportSession) return;
    clearPersistedJobId();
    const msg = e instanceof Error ? e.message : 'Import failed.';
    setSnapshot({
      phase: 'failed',
      jobId: null,
      error: msg,
      result: null,
      progress: null,
      partial: null,
    });
  }
}

/** Resume after refresh or new browser tab while a job was in flight. */
export function resumeGeminiExcelImportIfNeeded(): void {
  if (pollingActiveForSession != null) return;
  const jobId = readPersistedJobId();
  if (!jobId) return;
  console.log('[excel-import-manager] Resuming existing job:', { jobId });

  currentImportSession += 1;
  const session = currentImportSession;

  setSnapshot({
    phase: 'polling',
    jobId,
    error: null,
    result: null,
    progress: null,
    partial: null,
  });
  void runPollLoop(jobId, session).catch((err) => {
    console.error('Resume Gemini import failed', err);
    if (session !== currentImportSession) return;
    clearPersistedJobId();
    setSnapshot({
      phase: 'failed',
      jobId: null,
      error: err instanceof Error ? err.message : 'Import failed.',
      result: null,
      progress: null,
      partial: null,
    });
  });
}

export function resetGeminiImportOnLogout(): void {
  currentImportSession += 1;
  cancelGeminiImportPolling();
  clearPersistedJobId();
  setSnapshot({
    phase: 'idle',
    jobId: null,
    error: null,
    result: null,
    progress: null,
    partial: null,
  });
}

/** Clear completed result from memory after user is done reviewing (optional). */
export function clearGeminiImportCompletedResult(): void {
  if (snapshot.phase !== 'completed') return;
  setSnapshot({
    phase: 'idle',
    jobId: null,
    error: null,
    result: null,
    progress: null,
    partial: null,
  });
}
